import type { SectionProps } from "../context";
import { field, type RenderContext } from "../context";
import { imageSrc, srcSet } from "../helpers";
import { Container, Marquee, STACK, SectionHeader, SectionShell, SiteLink, Slideshow, cx } from "../ui";

type LogoItem = SectionProps<"logos">["node"]["content"]["items"][number];

/** Logos per slider page: a row of four on wide screens, a 2×2 block on phones. */
const PAGE = 4;

/** One logo in a fixed-height cell, so wide wordmarks and square marks sit on the same line. */
function Logo({ ctx, item, index, grey }: { ctx: RenderContext; item: LogoItem; index: number; grey: boolean }) {
  const src = item.logo ? imageSrc(item.logo, 480) : null;
  const mark = src ? (
    <img
      src={src}
      srcSet={item.logo ? srcSet(item.logo) : undefined}
      sizes="200px"
      alt={item.logo?.alt || item.name}
      width={item.logo?.width}
      height={item.logo?.height}
      loading="lazy"
      decoding="async"
      className="max-h-full w-auto max-w-[9.5rem] object-contain @3xl:max-w-[11rem]"
      {...field(ctx, `content.items.${index}.logo`)}
    />
  ) : (
    // No image yet: the name as a wordmark, in the heading font.
    <span className="baze-heading line-clamp-2 text-center text-lg leading-tight font-bold tracking-tight text-site-muted @3xl:text-xl" {...field(ctx, `content.items.${index}.name`)}>
      {item.name}
    </span>
  );
  const cell = cx(
    "flex h-14 min-w-0 items-center justify-center @3xl:h-16",
    grey && "opacity-70 grayscale transition duration-300 hover:opacity-100 hover:grayscale-0 motion-reduce:transition-none",
  );
  return item.url ? (
    <SiteLink ctx={ctx} href={item.url} className={cell} ariaLabel={item.name} trackLink>
      {mark}
    </SiteLink>
  ) : (
    <div className={cell}>{mark}</div>
  );
}

export function Logos({ node, ctx }: SectionProps<"logos">) {
  const c = node.content;
  const grey = !!c.grayscale;
  const items = c.items;
  const hasHeader = !!(c.title || c.eyebrow);
  const label = c.title || "Logo mitra";
  const logo = (item: LogoItem, i: number) => <Logo key={i} ctx={ctx} item={item} index={i} grey={grey} />;

  let body;
  if (items.length === 0) {
    body = ctx.editor ? <p className="rounded-site-md border border-dashed border-site-border p-8 text-center text-sm text-site-muted">Tambahkan logo mitra atau klien.</p> : null;
  } else if (node.variant === "marquee") {
    const row = <div className="flex shrink-0 items-center gap-14 pr-14 @3xl:gap-20 @3xl:pr-20">{items.map(logo)}</div>;
    body = (
      <Marquee seconds={Math.max(18, items.length * 5)} repeat={row} className="[mask-image:linear-gradient(90deg,transparent,#000_10%,#000_90%,transparent)]">
        {row}
      </Marquee>
    );
  } else if (node.variant === "slider" && items.length > PAGE) {
    const pages = Array.from({ length: Math.ceil(items.length / PAGE) }, (_, p) => items.slice(p * PAGE, p * PAGE + PAGE));
    body = (
      <Slideshow
        ctx={ctx}
        label={label}
        tone="light"
        interval={4500}
        // Arrows on the sides over the strip (dots kept for spacing, hidden).
        controlsClassName="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 justify-between [&>button]:pointer-events-auto [&>div]:invisible"
        slides={pages.map((page, p) => (
          // Flex, not grid: a short last page stays centred.
          <div className="flex flex-wrap items-center justify-center gap-y-8 px-12 @3xl:px-16">
            {page.map((item, i) => (
              <div key={i} className="basis-1/2 px-3 @3xl:basis-1/4">
                {logo(item, p * PAGE + i)}
              </div>
            ))}
          </div>
        ))}
      />
    );
  } else {
    body = <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-8 @3xl:gap-x-16">{items.map((item, i) => <div key={i} className="basis-[40%] @md:basis-auto">{logo(item, i)}</div>)}</div>;
  }

  return (
    <SectionShell ctx={ctx} node={node} labelledBy={!!c.title}>
      <Container size="wide">
        {/* A quiet caption over the logos, not a full section headline. */}
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align="center" className="mx-auto [&_.baze-title]:text-xl @3xl:[&_.baze-title]:text-[1.625rem]" />
        <div className={hasHeader ? STACK : undefined}>{body}</div>
      </Container>
    </SectionShell>
  );
}
